
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import GisMapView from './GisMapView';
import MapControls from './MapControls';
import RegionBreakdown from './RegionBreakdown';
import { useDeviceMap } from './useDeviceMap';
import { Device } from './types';

interface DeviceGisMapTabProps {
  deviceType?: 'energy' | 'gas' | 'water';
  onSelectDevice?: (deviceId: string, deviceType: 'smartMeter' | 'gateway' | 'voltageRegulator', deviceData?: Device) => void;
  onSwitchToDetailsTab?: () => void;
}

const DeviceGisMapTab: React.FC<DeviceGisMapTabProps> = ({ 
  deviceType = 'energy', 
  onSelectDevice,
  onSwitchToDetailsTab 
}) => {
  const {
    defaultCenter,
    zoom,
    showDevices,
    showConsumers,
    setShowDevices,
    setShowConsumers,
    filteredDevices,
    consumers,
    filterCategories,
    activeFilters,
    handleFilterChange,
    handleClearFilters,
    regions,
    deviceCounts
  } = useDeviceMap({ deviceType });

  // Handle device selection from the map
  const handleDeviceSelect = (deviceId: string, deviceType: 'smartMeter' | 'gateway' | 'voltageRegulator', deviceData?: Device) => {
    if (onSelectDevice) {
      onSelectDevice(deviceId, deviceType, deviceData);
      
      // Switch to the details tab if handler is provided
      if (onSwitchToDetailsTab) {
        onSwitchToDetailsTab();
      }
    }
  };

  return (
    <div className="h-full flex flex-col overflow-auto">
      <div className="p-2 sm:p-4 flex-shrink-0">
        <MapControls 
          filteredDevicesCount={filteredDevices.length}
          consumersCount={consumers.length}
          showDevices={showDevices}
          showConsumers={showConsumers}
          setShowDevices={setShowDevices}
          setShowConsumers={setShowConsumers}
          filterCategories={filterCategories}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
      </div>
      
      <Card className="flex-1 min-h-0 flex flex-col mx-2 sm:mx-4 mb-4">
        <CardHeader className="pb-3 flex-shrink-0">
          <CardTitle className="text-base sm:text-lg">GIS Map View - {deviceType.charAt(0).toUpperCase() + deviceType.slice(1)} Network</CardTitle>
          <div className="text-xs text-muted-foreground mt-1">
            <span className="font-medium">Device distribution: </span>
            Energy ({deviceCounts["Total"]["Energy"]}), 
            Gas ({deviceCounts["Total"]["Gas"]}), 
            Water ({deviceCounts["Total"]["Water"]})
          </div>
        </CardHeader>
        <CardContent className="flex-1 min-h-0 p-0 sm:p-4 flex flex-col">
          <div className="h-[300px] sm:h-[400px] md:h-[500px] rounded-md overflow-hidden border flex-grow">
            <GisMapView 
              defaultCenter={defaultCenter}
              zoom={zoom}
              showDevices={showDevices}
              showConsumers={showConsumers}
              filteredDevices={filteredDevices}
              consumers={consumers}
              onSelectDevice={handleDeviceSelect}
            />
          </div>
          
          <RegionBreakdown 
            deviceCounts={deviceCounts}
            regions={regions}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default DeviceGisMapTab;
